/**
 * Zustand store pro Tracer-AN nabíječ.
 *
 *  - `devices`: seznam z DB + runtime stav (status, deviceInfo, settings, statistics)
 *  - `telemetry`: poslední ~1 Hz snapshot per slug
 *  - `history`: posledních N (300) snapshotů per slug pro mini-grafy
 */

import { create } from "zustand";
import type {
  TracerListItem,
  TracerOverview,
  TracerRuntime,
  TracerStateWsEvent,
  TracerTelemetry,
} from "@client/types/tracer";

const MAX_LIVE_HISTORY = 300;

interface TracerStore {
  devices: TracerListItem[];
  telemetry: Map<string, TracerTelemetry>;
  overview: Map<string, TracerOverview>;
  history: Map<string, TracerTelemetry[]>;
  loaded: boolean;

  setDevices: (devices: TracerListItem[]) => void;
  applyTelemetryFull: (slug: string, t: TracerTelemetry) => void;
  applyTelemetryDiff: (slug: string, patch: Partial<TracerTelemetry>) => void;
  applyOverviewFull: (slug: string, o: TracerOverview) => void;
  applyOverviewDiff: (slug: string, patch: Partial<TracerOverview>) => void;
  applyState: (ev: TracerStateWsEvent) => void;
  clear: () => void;
}

export const useTracerStore = create<TracerStore>((set) => ({
  devices: [],
  telemetry: new Map(),
  overview: new Map(),
  history: new Map(),
  loaded: false,

  setDevices: (devices) => set({ devices, loaded: true }),

  applyTelemetryFull: (slug, t) =>
    set((state) => {
      const newTelemetry = new Map(state.telemetry);
      newTelemetry.set(slug, t);
      const newHistory = new Map(state.history);
      const prev = newHistory.get(slug) ?? [];
      newHistory.set(slug, [...prev, t].slice(-MAX_LIVE_HISTORY));
      return { telemetry: newTelemetry, history: newHistory };
    }),

  applyTelemetryDiff: (slug, patch) =>
    set((state) => {
      const prev = state.telemetry.get(slug);
      if (!prev) return state;
      const merged: TracerTelemetry = { ...prev, ...patch };
      const newTelemetry = new Map(state.telemetry);
      newTelemetry.set(slug, merged);
      const newHistory = new Map(state.history);
      const prevHist = newHistory.get(slug) ?? [];
      newHistory.set(slug, [...prevHist, merged].slice(-MAX_LIVE_HISTORY));
      return { telemetry: newTelemetry, history: newHistory };
    }),

  applyOverviewFull: (slug, o) =>
    set((state) => {
      const newOverview = new Map(state.overview);
      newOverview.set(slug, o);
      return { overview: newOverview };
    }),

  applyOverviewDiff: (slug, patch) =>
    set((state) => {
      const prev = state.overview.get(slug);
      if (!prev) return state;
      const newOverview = new Map(state.overview);
      newOverview.set(slug, { ...prev, ...patch });
      return { overview: newOverview };
    }),

  applyState: (ev) =>
    set((state) => {
      const devices = state.devices.map((d) => {
        if (d.slug !== ev.slug) return d;
        const runtime: TracerRuntime = d.runtime ?? {
          slug: d.slug,
          status: "idle",
          deviceInfo: null,
          settings: null,
          telemetry: null,
          statistics: null,
          lastPublishedAt: 0,
          lastTelemetryAt: 0,
        };
        return {
          ...d,
          runtime: {
            ...runtime,
            status: ev.status ?? runtime.status,
            deviceInfo: ev.deviceInfo ?? runtime.deviceInfo,
            settings: ev.settings ?? runtime.settings,
            statistics: ev.statistics ?? runtime.statistics,
          },
        };
      });
      return { devices };
    }),

  clear: () =>
    set({ devices: [], telemetry: new Map(), overview: new Map(), history: new Map(), loaded: false }),
}));
